import { defineStore } from 'pinia'
import { ofetch } from 'ofetch'
import type { PayloadVal } from '~/models/Search'

interface SearchState {
  countryID: string
}

export const useSearch = defineStore('search', {
  state: (): SearchState => ({
    countryID: useGetSubdomain(),
  }),

  actions: {
    getDateRange(option: string) {
      const now = new Date()
      let days

      switch (option) {
        case '24hours': days = 1; break
        case '7days': days = 7; break
        case '2weeks': days = 14; break
        case '1month': days = 30; break
        default: days = 0
      }

      const date_published_from = new Date(now.getTime() - days * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      const date_published_to = now.toISOString().split('T')[0]

      return { date_published_from, date_published_to }
    },
    extractJobInfo(url: string) {
      // Define the hardcoded types
      const jobTypes = ['full-time', 'part-time', 'freelance', 'internship']
      const workTypes = ['remote', 'onsite']
      let path = url.replace('/search/', '')

      const result = ref({
        jobTitle: null,
        location: null,
        jobType: null,
        workType: null,
      })

      // Detect job type and remove it
      const jobTypeFound = jobTypes.find(type => path.includes(type))
      if (jobTypeFound) {
        result.value.jobType = jobTypeFound
        path = path.replace(jobTypeFound, '').replace(/--/, '-').replace(/^-|-$/, '')
      }

      // Detect work type and remove it
      const workTypeFound = workTypes.find(type => path.includes(type))
      if (workTypeFound) {
        result.value.workType = workTypeFound
        path = path.replace(workTypeFound, '').replace(/--/, '-').replace(/^-|-$/, '')
      }

      // Detect location (matches "-in-{location}")
      const locationMatch = path.match(/-in-([a-zA-Z-]+)/)
      if (locationMatch) {
        result.value.location = locationMatch[1].replace(/-/g, ' ').replace(' jobs', '')
        path = path.replace(locationMatch[0], '').replace(/--/, '-').replace(/^-|-$/, '')
      }

      // The remaining path is the job title
      const cleanedPath = path.replace(/-/g, ' ').trim()
      if (cleanedPath && cleanedPath !== 'jobs')
        result.value.jobTitle = cleanedPath.replace(' jobs', '')

      return result.value
    },
    async getPayload(params: any, query: any): Promise<{ keyword: string | null; filter: any }> {
      const newFilter = this.extractJobInfo(params?.search.toString())
      if (query?.st === 'companies') {
        return {
          keyword: newFilter.jobTitle || query.q || '',
          filter: {
            location: newFilter.location ? newFilter.location.replace('-', '') : (!isEmpty(query.location) && query.location !== 'all') ? query.location.toString() : null,
            keyword: newFilter.jobTitle || query.q || '',
            industry: (!isEmpty(query.industry) && query.industry !== 'all') ? query.industry.toString() : null,
            from: query.page ? parseInt(query.page.toString()) : 1,
            size: 21,
          },
        }
      }
      else {
        return {
          keyword: newFilter.jobTitle,
          filter: {
            locations: newFilter.location ? [newFilter.location.replace('-', '')] : (!isEmpty(query.locations) && query.locations !== 'all' ? query.locations.toString().split(',') : []),
            keyword: newFilter.jobTitle,
            type: newFilter.jobType ? [newFilter.jobType.replace('-', '_')] : (!isEmpty(query.employment) && query.employment !== 'all' ? query.employment.toString().split(',') : []),
            remote: newFilter.workType ? newFilter.workType : (!isEmpty(query.work_type) && query.work_type !== 'all' ? query.work_type.toString() : null),
            date: !isEmpty(query.date_posted) && query.date_posted !== 'all' ? query.date_posted.toString() : null,
            from: query.page ? parseInt(query.page.toString()) : 1,
            size: 21,
            sort: query.sort ? query.sort.toString() : 'relevance',
          },
        }
      }
    },
    async getJob(payloadVal: PayloadVal, searchURL: string, searchKey: string, isSemantic: boolean) {
      return await ofetch(`${searchURL}/jobs/search`, {
        method: 'POST',
        headers: {
          'x-api-key': searchKey,
        },
        body: {
          country: this.countryID,
          cities: (isEmpty(payloadVal.locations) || payloadVal.locations[0] === 'all') ? '' : useTextToParam(payloadVal.locations, true).split(','),
          types: isEmpty(payloadVal.type) ? ['full_time', 'part_time', 'freelance', 'internship'] : payloadVal.type,
          remote: payloadVal.remote,
          date_published_from: payloadVal.date ? this.getDateRange(payloadVal.date).date_published_from : null,
          date_published_to: payloadVal.date ? this.getDateRange(payloadVal.date).date_published_to : null,
          term: payloadVal.keyword,
          from: (payloadVal.from - 1) * payloadVal.size,
          size: payloadVal.size,
          sort: payloadVal.sort,
          type: isSemantic ? 'sematic_search_with_similar_titles_search' : 'simple_title_with_similar_titles_search',
        },
      })
    },
    async getCompany(payloadVal: PayloadVal, searchURL: string) {
      return await ofetch(`${searchURL}/v2/companies/search`, {
        method: 'POST',
        body: {
          country: this.countryID,
          city: payloadVal.locations ? payloadVal.locations[0] : payloadVal.location || '',
          industry: !isEmpty(payloadVal.industry) ? payloadVal.industry : '',
          term: payloadVal.keyword,
          order: 'date',
          from: (payloadVal.from - 1) * payloadVal.size,
          size: payloadVal.size,
        },
      })
    },
    async getLoc(searchType: string, payloadVal: PayloadVal) {
      const locationList = ref([])
      const payload = searchType === 'jobs'
        ? {
            country: this.countryID,
            cities: (isEmpty(payloadVal.locations) || payloadVal.locations[0] === 'all') ? '' : useTextToParam(payloadVal.locations, true).split(','),
            types: isEmpty(payloadVal.type) ? ['full_time', 'part_time', 'freelance', 'internship'] : payloadVal.type,
            remote: payloadVal.remote,
            date_published_from: payloadVal.date ? this.getDateRange(payloadVal.date).date_published_from : null,
            date_published_to: payloadVal.date ? this.getDateRange(payloadVal.date).date_published_to : null,
            term: payloadVal.keyword,
            from: 0,
            size: 0,
            sort: 'relevance',
            stats: ['city'],
          }
        : {
            country: this.countryID,
            city: payloadVal.location,
            industry: !isEmpty(payloadVal.industry) ? payloadVal.industry : '',
            term: payloadVal.keyword,
            from: 0,
            size: 0,
            stats: ['city'],
          }

      try {
        const { data } = await useSearchApi(`${searchType === 'jobs' ? '/jobs/search' : '/v2/companies/search'}`, {
          method: 'POST',
          initialCache: false,
          body: payload,
          useAuth: true,
        })

        const resp = data?.value as { success: boolean; stats: { city: [] } } | null
        if (resp && resp.stats?.city) {
          locationList.value = resp.stats.city.map(({ value, count }) => ({
            label: useTextCapitalize(value),
            value: useTextToParam(value.toLowerCase(), false),
            count,
          }))
        }
        else if (this.countryID === 'hk') {
          locationList.value.push({ label: 'Hong Kong', value: 'all', count: 0 })
        }
      }
      catch (error) {
        console.error('Error fetching locations:', error)
      }

      return locationList.value
    },
  },

  persist: {
    key: 'search',
    storage: process.client ? window.localStorage : null,
  },
})
